import ApplicationStateApiService from "../services/apiServices/ApplicationStateApiService";
import { getIgnoreMaintenanceMode } from "../bridges/maintenanceBridge";
import { MAINTENANCE_MODE_DISABLED } from "../constants/applicationSettings";

/**
 * Relative path to the static maintenance page.
 *
 * IMPORTANT:
 * This path must be correct for the current deployment setup.
 * A wrong path will either cause broken redirects or redirect loops.
 */
const maintenancePagePath = import.meta.env.VITE_PATH_TO_MAINTENANCE_PAGE;

/**
 * Bootstraps the application with a maintenance-mode check.
 *
 * Depending on the backend state, this function either:
 * - allows the application to continue loading, or
 * - redirects the user to the maintenance page.
 *
 * This function is designed to run very early during startup,
 * before routes, auth guards, or feature initialization.
 */
export async function bootstrapMaintenanceOrRedirectAsync() {
    /**
     * STEP 1: Maintenance bypass check
     *
     * If a maintenance bypass is active (e.g. via secret URL token),
     * we immediately abort all maintenance logic.
     *
     * This is primarily used for:
     * - developers
     * - operators
     * - emergency access during outages
     */
    if (getIgnoreMaintenanceMode()) {
        console.log(
            "[Maintenance] Bypass detected – skipping maintenance check."
        );
        return;
    }

    try {
        /**
         * STEP 2: Fetch application settings from the backend
         *
         * This call is intentionally unauthenticated and uses a
         * short timeout, as it runs during application bootstrap.
         */
        const response = await ApplicationStateApiService.getAppSettings();

        // Safely extract maintenance mode from the response payload
        const mode = response?.data?.data?.maintenanceMode;
        const isNotMaintenance = mode === MAINTENANCE_MODE_DISABLED;
        if (isNotMaintenance) {
            console.log(
                "[Maintenance] Maintenance mode disabled – continuing app startup."
            );
            return;
        }

        console.log(
            "[Maintenance] Maintenance mode enabled – redirecting to maintenance page."
        );
        window.location.replace(maintenancePagePath);
    } catch (error) {
         console.error("[Maintenance] Status check failed:", error);

         /**
         * Redirect only on server-side errors (5xx).
         *
         * Client-side errors (4xx) or network issues should NOT
         * automatically trigger maintenance mode, as they may
         * be transient or configuration-related.
         */
        console.log(
            "[Maintenance] Unexepected error detected while getting application state – redirecting to maintenance page."
        );
        window.location.replace(maintenancePagePath);
    }
}
